/**
 * @fileoverview Shared, framework-agnostic MSAL token helpers used by every
 * FireSight web client (tenant-web, system-web, mobile-pwa).
 * @module auth
 *
 * Responsibilities:
 * - Decode a JWT's `exp` claim and decide if it is expired (DRY: previously
 *   copy-pasted as `isTokenExpired` / `isJwtExpired` in three apps).
 * - Acquire a fresh API bearer token from MSAL with a proactive expiry buffer
 *   and a forceRefresh fallback (the proven tenant-web pattern).
 *
 * Dependencies:
 * - `@azure/msal-browser` (types + acquireTokenSilent) as a peer dependency.
 *
 * Architecture:
 * - Pure logic only. NO React, NO app-specific provider/offline/impersonation
 *   concerns. Each app keeps its own AuthProvider and decides UX for
 *   InteractionRequiredAuthError (which this module rethrows to the caller).
 * - Consumed as TypeScript source (main/types → src/index.ts), mirroring
 *   `@firesquirrel/local-db`.
 *
 * Notes:
 * - The idToken (aud === clientId) is the API token validated by the FireSight
 *   APIs; the accessToken targets Microsoft Graph. Callers prefer idToken.
 */

import type {
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
  RedirectRequest,
} from '@azure/msal-browser';

// Dev/stage auth-bypass identity constants — single source shared by
// system-web, tenant-web and mobile-pwa. API-side counterparts live in
// @firesquirrel/core (api/packages/core/src/devBypass.ts).
export {
  DEV_BYPASS_TOKEN,
  DEV_BYPASS_ACTOR_ID,
  DEV_BYPASS_USERNAME,
  DEV_BYPASS_EMAIL,
  DEV_BYPASS_DISPLAY_NAME,
} from './devBypass';

/**
 * @contract ProviderType
 * Purpose: Identifies a configured federated sign-in provider.
 * Fields:  Provider discriminator.
 * Rules:   Only the Microsoft Entra OIDC adapter is currently available.
 * Sync:    Shared by tenant-web and mobile-pwa redirect construction.
 */
export type ProviderType = 'microsoft_entra_oidc' | 'google_workspace_oidc' | 'generic_oidc' | 'saml2';

/**
 * @contract LoginSyncErrorContent
 * Purpose: Defines safe, actionable copy for server-denied tenant sign-ins.
 * Fields:  title, message, nextStep.
 * Rules:   Copy must not expose internal server details or identity claims.
 * Sync:    Pure presentation data shared across tenant-facing web clients.
 */
export interface LoginSyncErrorContent {
  title: string;
  message: string;
  nextStep: string;
}

const LOGIN_SYNC_ERROR_CONTENT: Record<string, LoginSyncErrorContent> = {
  MEMBERSHIP_PENDING_APPROVAL: {
    title: 'Approval pending',
    message: 'Your account is waiting for approval from your department administrator.',
    nextStep: 'Contact your department administrator for access updates.',
  },
  LICENSE_LIMIT_EXCEEDED: {
    title: 'Department seats are full',
    message: 'This department has reached its current licensed user limit.',
    nextStep: 'Ask your department administrator to review available seats.',
  },
  WRONG_IDENTITY_PROVIDER: {
    title: 'Wrong organization account',
    message: 'This account belongs to an organization that is not configured for this department.',
    nextStep: 'Sign in with the account provided by your department.',
  },
  SSO_REQUIRED: {
    title: 'Organizational sign-in required',
    message: 'This department requires sign-in through its configured organization.',
    nextStep: 'Use the department’s organizational sign-in button.',
  },
  ACCOUNT_REQUIRES_ADMIN_RELINK: {
    title: 'Administrator help required',
    message: 'This profile is linked to a previous organization directory account.',
    nextStep: 'Ask your department administrator to confirm the account relink.',
  },
  USER_SUSPENDED: {
    title: 'Account deactivated',
    message: 'Your Defensible Connect account has been deactivated.',
    nextStep: 'Contact your department administrator for assistance.',
  },
  AUTH_METHOD_MISMATCH: {
    title: 'Sign-in method not available',
    message: 'This department uses a different sign-in method for its accounts.',
    nextStep: 'Use the sign-in method shown for this department.',
  },
};

/**
 * @process BuildFederatedLoginRedirectRequest
 *
 * Steps:
 * 1. Verify the configured provider has an implemented adapter.
 * 2. Construct an MSAL redirect request from scopes and optional hints/state.
 * 3. Add the Entra domain hint only for Microsoft Entra OIDC.
 *
 * Inputs:
 * - Provider type, scopes, and optional domain/login hints and state.
 *
 * Outputs:
 * - A redirect request accepted by MSAL Browser.
 *
 * Failure Handling:
 * - Unsupported provider types throw PROVIDER_NOT_AVAILABLE.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function buildFederatedLoginRedirectRequest(input: {
  providerType: ProviderType;
  scopes: string[];
  domainHint?: string;
  loginHint?: string;
  state?: string;
}): RedirectRequest {
  if (input.providerType !== 'microsoft_entra_oidc') {
    throw new Error('PROVIDER_NOT_AVAILABLE');
  }

  return {
    scopes: input.scopes,
    ...(input.loginHint ? { loginHint: input.loginHint } : {}),
    ...(input.state ? { state: input.state } : {}),
    ...(input.domainHint ? { extraQueryParameters: { domain_hint: input.domainHint } } : {}),
  };
}

/**
 * Resolve shared user-facing content for a login-sync denial.
 *
 * @param code Server-provided error code; never use raw response text as copy.
 * @returns Safe copy for a known code, or null for an unknown code.
 */
export function getLoginSyncErrorContent(code: string | undefined): LoginSyncErrorContent | null {
  return code ? LOGIN_SYNC_ERROR_CONTENT[code] ?? null : null;
}

/**
 * Decode the base64url payload segment of a JWT. Returns null on any failure.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * @contract isJwtExpired
 * Purpose: Single source of truth for "is this bearer token unusable?".
 * Fields:  token (JWT string), bufferSeconds (clock-skew safety margin).
 * Rules:
 *  - Malformed/unparseable token ⇒ treated as EXPIRED (true). Sending it would
 *    only earn a 401, so callers must keep waiting for a real token.
 *  - A token without an `exp` claim ⇒ treated as NOT expired (false), matching
 *    the prior per-app behavior.
 *  - Otherwise expired when (exp - bufferSeconds) <= now.
 * Sync:    Used by the api-client gate and the AuthProvider session checks.
 */
export function isJwtExpired(token: string, bufferSeconds = 30): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  const exp = payload.exp as number | undefined;
  if (!exp) return false;
  return exp - bufferSeconds < Date.now() / 1000;
}

/**
 * Options controlling proactive refresh behavior for {@link acquireFreshToken}.
 */
export interface AcquireFreshTokenOptions {
  /**
   * Force a network token renewal when the acquired token is within this many
   * milliseconds of its `exp`. Defaults to 5 minutes — identical to tenant-web.
   */
  proactiveRefreshBufferMs?: number;
  /**
   * When true, the FIRST acquireTokenSilent call is made with
   * `forceRefresh: true` (a caller explicitly demanded a network renewal,
   * e.g. system-web's token provider `options.forceRefresh`). The proactive
   * buffer check still applies afterward. Defaults to false.
   */
  forceRefresh?: boolean;
}

/**
 * @process acquireFreshToken
 *
 * Steps:
 * 1. Silently acquire a token for the given account/scopes.
 * 2. Prefer the idToken (aud === clientId, validated by FireSight APIs); fall
 *    back to the accessToken.
 * 3. If that token is within the proactive buffer of expiry, re-acquire with
 *    `forceRefresh: true` so we never hand a caller a token that is about to /
 *    has already expired (the root cause of the mobile 401 NO_TOKEN storms).
 *
 * Inputs:
 * - instance: an initialized MSAL PublicClientApplication.
 * - account:  the signed-in MSAL account.
 * - scopes:   login scopes (e.g. ['openid','profile','email']).
 * - opts:     optional proactive refresh buffer.
 *
 * Outputs:
 * - A fresh bearer token string, or null when MSAL returned no token.
 *
 * Failure Handling:
 * - Does NOT swallow errors. `acquireTokenSilent` throws (notably
 *   InteractionRequiredAuthError) propagate to the caller so each app applies
 *   its own offline guard and session-expired UX.
 *
 * Idempotency:
 * - REQUIRED (safe to call on every request; this is the pull model).
 */
export async function acquireFreshToken(
  instance: IPublicClientApplication,
  account: AccountInfo,
  scopes: string[],
  opts?: AcquireFreshTokenOptions,
): Promise<string | null> {
  let response: AuthenticationResult = await instance.acquireTokenSilent({
    scopes,
    account,
    forceRefresh: opts?.forceRefresh ?? false,
  });
  let token = response.idToken || response.accessToken;

  if (token) {
    // Only force a refresh when we can actually read an `exp` AND it is within
    // the buffer. A token whose payload cannot be decoded is used as-is — this
    // matches the original per-app behavior (decode failure ⇒ no forced
    // refresh) and avoids hammering acquireTokenSilent on opaque tokens.
    const payload = decodeJwtPayload(token);
    const exp = payload?.exp as number | undefined;
    if (exp) {
      const expiresAtMs = exp * 1000;
      const bufferMs = opts?.proactiveRefreshBufferMs ?? 5 * 60 * 1000;
      if (Date.now() > expiresAtMs - bufferMs) {
        response = await instance.acquireTokenSilent({ scopes, account, forceRefresh: true });
        token = response.idToken || response.accessToken;
      }
    }
  }

  return token ?? null;
}
